import { Injectable } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { BreadcrumbItem } from '../models/breadcrumb-item';
import { AppMenuItem } from '@nx-ahs/shared/models';
import { AppMenuService } from '@nx-ahs/shared/services';
import { SecurityService } from './security.service';
import { TaskService } from './task.service';

@Injectable({
	providedIn: 'root'
})
export class BreadcrumbService {
	public breadcrumbArray: BehaviorSubject<BreadcrumbItem[]> = new BehaviorSubject<BreadcrumbItem[]>([]);

	private internalArray: BreadcrumbItem[] = [];
	private firstInput: BreadcrumbItem;
	private subscriptionArray: Subscription[] = [];
	private localStorageKey: string;

	constructor(
		private appMenuService: AppMenuService,
		private securityService: SecurityService,
		private taskService: TaskService
	) {
		this.securityService.appChanged.subscribe((app) => {
			this.localStorageKey = `breadcrumbs-${app.application.applicationId}`;
			// attempt to get the breadcrumbs from local storage
			const storedBreadcrumbs = JSON.parse(localStorage.getItem(this.localStorageKey));
			if (storedBreadcrumbs) {
				this.internalArray = storedBreadcrumbs;
				this.breadcrumbArray.next(this.internalArray);
			}
		});

		if (this.subscriptionArray.length <= 0) {
			// I think this is an okay way to prevent multiple subscriptions
			this.initialize();
		}
	}

	public menuToBreadcrumb(item: AppMenuItem) {
		// find the breadcrumb level by the indexId decimal points
		const splitIndexId = item.indexId.split('.');
		let indexLevel;
		if (splitIndexId[2] == '0') {
			indexLevel = splitIndexId.length - 2 - 1; // custom inboxes follow the scheme of 0.${number}, so we want to remove that 0 at the 3rd position from our indexLevel calculation
		} else {
			indexLevel = splitIndexId.length - 1 - 1;
		}
		for (let i = 0; i < splitIndexId.length; i++) {
			// rebuild the array from the start here
			if (i === 0) {
				// put in the first input
				this.internalArray.splice(0);
			}
			// get the index for the next item in the menu. Do this by adding the next segment from splitIndexId
			const slice: string[] = splitIndexId.slice(0, i + 2);
			const indexToFind: string = slice.join('.');

			let midwayMenuItem: AppMenuItem;
			if (this.internalArray.length && this.internalArray[i - 1].menuItem?.childMenu) {
				midwayMenuItem = this.internalArray[i - 1].menuItem.childMenu.find((m) => m.indexId === indexToFind);
			} else {
				// this is probably the first item, which has no menu item
				midwayMenuItem = this.appMenuService.appMenuConfig.find((m) => m.indexId === indexToFind);
			}

			let midwayIndexLevel;
			if (i === 2 && slice[2] === '0') {
				midwayIndexLevel = slice.length - 2 - 1;
			} else {
				midwayIndexLevel = slice.length - 1 - 1;
			}
			const midwayParentMenu = this.internalArray[midwayIndexLevel - 1]?.menuItem
				? this.internalArray[midwayIndexLevel - 1]?.menuItem.childMenu
				: this.firstInput.parentMenu; // -1 on the index because we want the menu from the parent item. This assumes that a -1 index means we have an empty array and want the base menu
			const midwayNewBreadcrumb: BreadcrumbItem = {
				menuItem: midwayMenuItem,
				arrayIndex: midwayIndexLevel,
				parentMenu: midwayParentMenu,
				isInput: false
			};
			this.internalArray.push(midwayNewBreadcrumb);
		}
		const parentMenu = this.internalArray[indexLevel]?.parentMenu;
		const newBreadcrumb: BreadcrumbItem = {
			menuItem: item,
			arrayIndex: indexLevel,
			parentMenu: parentMenu,
			isInput: false
		};
		this.rebuildBreadcrumbArray(newBreadcrumb, true);
	}

	public rebuildBreadcrumbArray(newBreadcrumb: BreadcrumbItem, fromMainMenu: boolean = false) {
		// first scenario: add the item to the end of the list
		// second scenario: splice the item into the list and remove all items past it

		// splice the array at the item's arrayIndex, removing all children after it
		this.internalArray.splice(newBreadcrumb.arrayIndex);
		this.internalArray.push(newBreadcrumb);

		// set menu items closed for every breadcrumb beyond the splice point before splicing
		for (let i = newBreadcrumb.arrayIndex; i + 1 < this.internalArray.length; i++) {
			this.internalArray[i + 1].menuItem.expanded = false;
		}
		// follow-up action: add an input if the item has a child menu
		if (newBreadcrumb.menuItem.childMenu?.length) {
			const newInput: BreadcrumbItem = {
				menuItem: {
					menuItemId: null,
					iconId: null,
					title: null
				},
				parentMenu: newBreadcrumb.menuItem.childMenu,
				isInput: true,
				arrayIndex: this.internalArray.length
			};
			this.internalArray.push(newInput);
		}
		if (!fromMainMenu) this.breadcrumbArray.next(this.internalArray);
		this.storeBreadcrumbs();
	}

	public resetBreadcrumbArray() {
		this.rebuildBreadcrumbArray(this.firstInput);
		this.storeBreadcrumbs();
	}

	private initialize() {
		this.subscriptionArray.push(
			this.appMenuService.appMenuUpdated.subscribe((val) => {
				this.firstInput = {
					menuItem: {
						menuItemId: null,
						iconId: null,
						title: null
					},
					parentMenu: val,
					isInput: true,
					arrayIndex: 0
				};
				if (this.internalArray.length == 0) {
					this.internalArray.push(this.firstInput);
					this.breadcrumbArray.next(this.internalArray);
				}
			})
		);
		this.subscriptionArray.push(
			this.taskService.taskInboxesLoadedEvent.subscribe((val) => {
				this.internalArray[0].parentMenu.find((x) => x.url === 'task-inbox').childMenu = val; // task inboxes load whenever during the app initialization, so we'll try to update them when that happens
				this.storeBreadcrumbs();
				this.breadcrumbArray.next(this.internalArray);
			})
		);
	}

	private storeBreadcrumbs() {
		localStorage.setItem(this.localStorageKey, JSON.stringify(this.internalArray));
	}
}
